import { logEvent } from "@shared/analytics/analytics";
import { DeleteConfirmationDialog } from "@shared/components/deleteComformationModal";
import { Button } from "@shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { toast } from "@shared/ui/use-toast";
import { Edit, FileText, Trash2 } from "lucide-react";
import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDeleteNetwork } from "../../hooks"; // Adjust import path as needed

export const NetworkQuickActionsSidebar: React.FC<{ data: any }> = ({
  data,
}) => {
  const navigate = useNavigate();
  const { id } = useParams();

  // Delete confirmation dialog state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [networkToDelete, setNetworkToDelete] = useState<any>(null);

  const handleDeleteClick = () => {
    setNetworkToDelete(data);
    setShowDeleteDialog(true);
  };

  const { mutate: deleteNetworkMutation, isPending: isDeletingNetwork } =
    useDeleteNetwork();

  const handleConfirmDelete = () => {
    if (networkToDelete) {
      logEvent("Network Delete Confirmed", {
        networkId: networkToDelete.id,
      });
      deleteNetworkMutation(
        { networkId: networkToDelete.id },
        {
          onSuccess: () => {
            toast({
              title: "Success",
              description: `"${networkToDelete.networkName}" has been deleted successfully.`,
            });
            setShowDeleteDialog(false);
            setNetworkToDelete(null);
            navigate("/ax/asset-registry/networks");
          },
          onError: (error) => {
            toast({
              title: "Delete Failed",
              description: error.message,
              variant: "destructive",
            });
          },
        }
      );
    }
  };

  const handleDeleteDialogClose = () => {
    setShowDeleteDialog(false);
    setNetworkToDelete(null);
  };

  return (
    <div>
      <Card className="h-fit">
        <CardHeader>
          <CardTitle className="text-lg font-semibold">Quick Actions</CardTitle>
          <p className="text-sm text-gray-600">
            Common network management tasks
          </p>
        </CardHeader>

        <CardContent className="space-y-3 flex flex-col">
          <Button
            variant="outline"
            className="w-full justify-start items-center h-12"
            size="sm"
            onClick={() =>
              navigate(`/ax/asset-registry/networks/create/edit/${id}`)
            }
          >
            <Edit className="h-4 w-4 mr-2" />
            <span className="truncate text-left">Edit Network</span>
          </Button>

          <Button
            variant="outline"
            className="w-full justify-start items-center h-12"
            size="sm"
            onClick={() => navigate("/ax/service-order/landing/create")}
          >
            <FileText className="h-4 w-4 mr-2" />
            <span className="text-left">Create Service Order</span>
          </Button>

          <Button
            variant="outline"
            className="w-full justify-start items-center h-12"
            size="sm"
            onClick={handleDeleteClick}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            <span className="truncate text-left">Delete</span>
          </Button>
        </CardContent>
      </Card>

      <DeleteConfirmationDialog
        isOpen={showDeleteDialog}
        onClose={handleDeleteDialogClose}
        onConfirm={handleConfirmDelete}
        title="Delete Network"
        itemName={data?.name}
        description={`Are you sure you want to delete the network "${data?.networkName}"? This will permanently remove the network and all its associated data. This action cannot be undone.`}
        isLoading={isDeletingNetwork}
      />
    </div>
  );
};
